import React from 'react';
import { useCalculator } from '../../context/CalculatorContext';
import { formatCurrency } from '../../utils/formatters';
import { Settings, ChevronRight, ChevronLeft, TrendingUp, DollarSign, BarChart3, Plus, Minus, Zap, Target, RefreshCw, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
import ChartSeriesToggleCard from '../ui/ChartSeriesToggleCard';
import { Toggle } from '../../components/ui/Toggle';

interface ReinvestmentSpecificDataProps {
  onNext: () => void;
  onPrevious: () => void;
}

const ReinvestmentSpecificData: React.FC<ReinvestmentSpecificDataProps> = ({ onNext, onPrevious }) => {
  const { investment, updateInvestment, toggleLemonPrice, toggleTaxes, toggleReinvest } = useCalculator();

  // Handlers for increment/decrement buttons
  const handleIncrementInflation = () => {
    if (investment.inflationRate < 15) {
      updateInvestment({ inflationRate: Math.round((investment.inflationRate + 0.5) * 10) / 10 });
    }
  };

  const handleDecrementInflation = () => {
    if (investment.inflationRate > 0) {
      updateInvestment({ inflationRate: Math.round((investment.inflationRate - 0.5) * 10) / 10 });
    }
  };

  const handleIncrementLemonPrice = () => {
    if (investment.lemonPriceIncrease < 10) {
      updateInvestment({ lemonPriceIncrease: Math.round((investment.lemonPriceIncrease + 0.5) * 10) / 10 });
    }
  };

  const handleDecrementLemonPrice = () => {
    if (investment.lemonPriceIncrease > 0) {
      updateInvestment({ lemonPriceIncrease: Math.round((investment.lemonPriceIncrease - 0.5) * 10) / 10 });
    }
  };

  const handleIncrementTaxRate = () => {
    if (investment.taxRate < 50) {
      updateInvestment({ taxRate: investment.taxRate + 1 });
    }
  };

  const handleDecrementTaxRate = () => {
    if (investment.taxRate > 0) {
      updateInvestment({ taxRate: investment.taxRate - 1 });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-4xl mx-auto"
    >
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-8 text-white">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <Settings className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Configuración de Reinversión</h2>
              <p className="text-purple-100">Ajusta parámetros específicos para maximizar el crecimiento de tu inversión</p>
            </div>
          </div>
        </div>
        
        <div className="p-8 space-y-8">
          {/* Reinvestment Options */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Opciones de Reinversión</h3>
            </div>
            
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-green-800">Reinvertir Utilidades</h4>
                  <p className="text-sm text-green-600">
                    {investment.reinvestProfits 
                      ? "En el mes 49, podrás elegir destinar las utilidades para adquirir más certificados siguiendo este plan" 
                      : "En el mes 49, elegirías recibir las utilidades como ingreso mensual"}
                  </p>
                </div>
                <Toggle
                  label=""
                  checked={investment.reinvestProfits}
                  onChange={toggleReinvest}
                />
              </div>
            </div>
          </div>

          {/* Inflation Rate */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Tasa de Inflación</h3>
            </div>
            
            <div className="flex items-center justify-center gap-4 mb-5">
              <button 
                onClick={handleDecrementInflation}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={investment.inflationRate <= 0}
              >
                <Minus className="w-5 h-5 text-gray-700" />
              </button>
              <div className="text-center">
                <input
                  type="number"
                  min="0"
                  max="15"
                  step="0.5"
                  value={investment.inflationRate}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value) && value >= 0 && value <= 15) {
                      updateInvestment({ inflationRate: value });
                    }
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-20 text-center text-3xl font-bold text-gray-900 mb-1 border-0 focus:ring-0 focus:outline-none"
                />
                <div className="text-sm text-orange-600">
                  inflación anual
                </div>
              </div>
              <button 
                onClick={handleIncrementInflation}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={investment.inflationRate >= 15}
              >
                <Plus className="w-5 h-5 text-gray-700" />
              </button>
            </div>

            <div className="space-y-3">
              <input
                type="range"
                min="0"
                max="15"
                step="0.5"
                value={investment.inflationRate}
                onChange={(e) => updateInvestment({ inflationRate: parseFloat(e.target.value) })}
                className="w-full h-2 bg-gradient-to-r from-orange-200 to-orange-300 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>0%</span>
                <span>15%</span>
              </div>
            </div>
          </div>

          {/* Lemon Price Increase */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-yellow-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">Incremento Precio Limón</h3>
              </div>
              <ChartSeriesToggleCard
                label=""
                icon={TrendingUp}
                color="#f59e0b"
                checked={investment.increaseLemonPrice}
                onClick={toggleLemonPrice}
                className="w-32"
              />
            </div>
            
            {investment.increaseLemonPrice && (
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-4 mb-5">
                  <button 
                    onClick={handleDecrementLemonPrice}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    disabled={investment.lemonPriceIncrease <= 0}
                  >
                    <Minus className="w-5 h-5 text-gray-700" />
                  </button>
                  <div className="text-center">
                    <input
                      type="number"
                      min="0"
                      max="10"
                      step="0.5"
                      value={investment.lemonPriceIncrease}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value) && value >= 0 && value <= 10) {
                          updateInvestment({ lemonPriceIncrease: value });
                        }
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="w-20 text-center text-3xl font-bold text-gray-900 mb-1 border-0 focus:ring-0 focus:outline-none"
                    />
                    <div className="text-sm text-yellow-600">
                      incremento anual
                    </div>
                  </div>
                  <button 
                    onClick={handleIncrementLemonPrice}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    disabled={investment.lemonPriceIncrease >= 10}
                  >
                    <Plus className="w-5 h-5 text-gray-700" />
                  </button>
                </div>

                <div className="space-y-3">
                  <input
                    type="range"
                    min="0"
                    max="10"
                    step="0.5"
                    value={investment.lemonPriceIncrease}
                    onChange={(e) => updateInvestment({ lemonPriceIncrease: parseFloat(e.target.value) })}
                    className="w-full h-2 bg-gradient-to-r from-yellow-200 to-yellow-300 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>0%</span>
                    <span>10%</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Taxes */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">Aplicar Impuestos</h3>
              </div>
              <ChartSeriesToggleCard
                label=""
                icon={DollarSign}
                color="#dc2626"
                checked={investment.applyTaxes}
                onClick={toggleTaxes}
                className="w-32"
              />
            </div>
            
            {investment.applyTaxes && (
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-4 mb-5">
                  <button 
                    onClick={handleDecrementTaxRate}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    disabled={investment.taxRate <= 0}
                  >
                    <Minus className="w-5 h-5 text-gray-700" />
                  </button>
                  <div className="text-center">
                    <input
                      type="number"
                      min="0"
                      max="50"
                      step="1"
                      value={investment.taxRate}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (!isNaN(value) && value >= 0 && value <= 50) {
                          updateInvestment({ taxRate: value });
                        }
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="w-20 text-center text-3xl font-bold text-gray-900 mb-1 border-0 focus:ring-0 focus:outline-none"
                    />
                    <div className="text-sm text-red-600">
                      tasa de impuestos
                    </div>
                  </div>
                  <button 
                    onClick={handleIncrementTaxRate}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    disabled={investment.taxRate >= 50}
                  >
                    <Plus className="w-5 h-5 text-gray-700" />
                  </button>
                </div>

                <div className="space-y-3">
                  <input
                    type="range"
                    min="0"
                    max="50"
                    step="1"
                    value={investment.taxRate}
                    onChange={(e) => updateInvestment({ taxRate: parseInt(e.target.value) })}
                    className="w-full h-2 bg-gradient-to-r from-red-200 to-red-300 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>0%</span>
                    <span>50%</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Payment Boost */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Zap className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">Boost de Pago</h3>
              </div>
              <ChartSeriesToggleCard
                label=""
                icon={Zap}
                color="#8b5cf6"
                checked={investment.enablePaymentBoost || false}
                onClick={() => updateInvestment({ enablePaymentBoost: !investment.enablePaymentBoost })}
                className="w-32"
              />
            </div>

            {investment.enablePaymentBoost ? (
              <div className="p-4 bg-purple-50 rounded-lg">
                <h4 className="font-medium text-purple-800 mb-2">Boost de Pago Activado</h4>
                <p className="text-sm text-purple-700 mb-2">
                  Una vez que los certificados iniciales estén 100% pagados, continuarás aportando el equivalente 
                  a la mensualidad original para acelerar la adquisición de nuevos certificados.
                </p>
                <div className="bg-white p-3 rounded-lg">
                  <div className="text-xs text-purple-600 mb-1">Mensualidad original calculada:</div>
                  <div className="text-lg font-bold text-purple-700">
                    {formatCurrency(
                      (investment.initialCertificates * investment.certificateBasePrice * 0.7) / 48,
                      investment.currencyFormat
                    )}
                  </div>
                  <div className="text-xs text-purple-600">
                    mensuales ({formatCurrency(
                      (investment.initialCertificates * investment.certificateBasePrice * 0.7) / 4,
                      investment.currencyFormat
                    )} anuales)
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-700 mb-2">¿Qué es el Boost de Pago?</h4>
                <p className="text-sm text-gray-600">
                  Una vez que termines de pagar tus certificados iniciales, puedes continuar aportando 
                  para acelerar dramáticamente la adquisición de nuevos certificados.
                </p>
              </div>
            )}
          </div>
          
          {/* Navigation Buttons */}
          <div className="flex justify-between">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onPrevious}
              className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-all duration-300 flex items-center gap-2"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Anterior</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onNext}
              className="px-8 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
            >
              <span>Continuar</span>
              <ChevronRight className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ReinvestmentSpecificData;